import { useState, useEffect, Fragment } from "react";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import ThirdPartyServices from "../../services/ThirdParty";
import fire from "../../utils/Toastr";
import { HollowDotsSpinner } from "react-epic-spinners";

export default function CreatePayrollOfficerModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  organizationId,
  organizations 
}) {
  const [formData, setFormData] = useState({
    tenantId: "",
    bvn: "",
    emailAddress: "",
    phoneNumber: "",
    organizationId: organizationId || "",
    officerType: "Maker",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState("");

  const filteredOrganizations = selectedAppId
    ? organizations.filter(org => {
        const app = applications.find(a => a.applicationId === selectedAppId);
        return app ? org.applicationName === app.applicationName : false;
      })
    : [];

  // Update form when organizationId prop changes and auto-populate tenantId
  useEffect(() => {
    if (organizationId) {
      setFormData(prev => ({
        ...prev,
        organizationId: organizationId,
      }));
      autoPopulateTenantId(organizationId);
    } else {
      setFormData(prev => ({ ...prev, tenantId: "" }));
    }
  }, [organizationId]);

  const autoPopulateTenantId = async (orgId) => {
    if (!orgId || !organizations?.length) return;

    const org = organizations.find(o => o.organizationId === orgId);
    if (!org?.applicationName) return;

    setApplicationsLoading(true);
    try {
      const response = await ThirdPartyServices.listApplications(1, 100);
      if (response.status && response.data?.result) {
        setApplications(response.data.result);
        const match = response.data.result.find(
          app => app.applicationName === org.applicationName
        );
        if (match?.tenantId) {
          setFormData(prev => ({ ...prev, tenantId: match.tenantId }));
        }
      }
    } catch (err) {
      console.error("Error fetching tenantId:", err);
    } finally {
      setApplicationsLoading(false);
    }
  };

  // Re-populate tenantId when modal opens with a selected organization
  useEffect(() => {
    if (isOpen && organizationId) {
      autoPopulateTenantId(organizationId);
    }
  }, [isOpen]);

  // Re-populate tenantId when organizations list changes (e.g., after full fetch)
  useEffect(() => {
    if (organizationId && organizations?.length) {
      const org = organizations.find(o => o.organizationId === organizationId);
      if (org?.applicationName && applications?.length) {
        const match = applications.find(
          app => app.applicationName === org.applicationName
        );
        if (match?.tenantId) {
          setFormData(prev => ({ ...prev, tenantId: match.tenantId }));
        }
      }
    }
  }, [organizations, applications, organizationId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tenantId.trim()) {
      newErrors.tenantId = "Tenant ID is required";
    }

    if (!formData.bvn.trim()) {
      newErrors.bvn = "BVN is required";
    } else if (!/^\d{11}$/.test(formData.bvn.trim())) {
      newErrors.bvn = "BVN must be 11 digits";
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = "Please enter a valid email address";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{11}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = "Phone number must be 11 digits";
    }

    if (!formData.organizationId) {
      newErrors.organizationId = "Organization is required";
    }

    if (!formData.officerType) {
      newErrors.officerType = "Officer type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      fire("error", "Please fix the validation errors");
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.createPayrollOfficer(formData);

      if (response.status) {
        fire("success", response.message || "Payroll officer created successfully!");
        onSuccess();
        handleClose();
      } else {
        fire("error", response.message || "Failed to create payroll officer");
      }
    } catch (error) {
      console.error("Error creating payroll officer:", error);
      fire("error", "An error occurred while creating the payroll officer");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      tenantId: "",
      bvn: "",
      emailAddress: "",
      phoneNumber: "",
      organizationId: organizationId || "",
      officerType: "Maker",
    });
    setErrors({});
    setApplications([]);
    setSelectedAppId("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white p-6">
          <div className="flex justify-between items-center mb-6">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Add New Payroll Officer
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Application Selection */}
            {!organizationId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application *
                </label>
                <select
                  value={selectedAppId}
                  onChange={(e) => {
                    setSelectedAppId(e.target.value);
                    setFormData(prev => ({ ...prev, organizationId: "" }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select Application</option>
                  {applications.map((app) => (
                    <option key={app.applicationId} value={app.applicationId}>
                      {app.applicationName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Organization Selection (if not auto-selected) */}
            {!organizationId && (
              <div>
                <label
                  htmlFor="organizationId"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Organization *
                </label>
                <select
                  id="organizationId"
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md text-sm ${
                    errors.organizationId ? "border-red-300" : "border-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                  disabled={loading || !selectedAppId}
                >
                  <option value="">{selectedAppId ? "Select Organization" : "Select an application first"}</option>
                  {filteredOrganizations?.map((org) => (
                    <option key={org.organizationId} value={org.organizationId}>
                      {org.companyName}
                    </option>
                  ))}
                </select>
                {errors.organizationId && (
                  <p className="mt-1 text-sm text-red-600">{errors.organizationId}</p>
                )}
              </div>
            )}

            {/* BVN */}
            <div>
              <label
                htmlFor="bvn"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                BVN (11 digits) *
              </label>
              <input
                type="text"
                id="bvn"
                name="bvn"
                value={formData.bvn}
                onChange={handleChange}
                maxLength={11}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.bvn ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter 11-digit BVN"
                disabled={loading}
              />
              {errors.bvn && (
                <p className="mt-1 text-sm text-red-600">{errors.bvn}</p>
              )}
            </div>

            {/* Email Address */}
            <div>
              <label
                htmlFor="emailAddress"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address *
              </label>
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.emailAddress ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter email address"
                disabled={loading}
              />
              {errors.emailAddress && (
                <p className="mt-1 text-sm text-red-600">{errors.emailAddress}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number (11 digits) *
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                maxLength={11}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.phoneNumber ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter 11-digit phone number"
                disabled={loading}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Officer Type */}
            <div>
              <label
                htmlFor="officerType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Officer Type *
              </label>
              <select
                id="officerType"
                name="officerType"
                value={formData.officerType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.officerType ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                disabled={loading}
              >
                <option value="Maker">Maker</option>
                <option value="Checker">Checker</option>
              </select>
              {errors.officerType && (
                <p className="mt-1 text-sm text-red-600">{errors.officerType}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Maker: Can create payrolls | Checker: Can approve payrolls
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <HollowDotsSpinner
                      color="#ffffff"
                      size={20}
                      className="mr-2"
                    />
                    Creating...
                  </>
                ) : (
                  "Create Payroll Officer"
                )}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}